import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { StepsForm } from '~/components';

import { FirstStep, SecondStep, ThirdStep, FourthStep } from './components';
import { useRoute, type RouteProp } from '@react-navigation/native';
// import { Logo } from '~/components/Header';
import ExitConfirmationModal from '../components/ExitConfirmationModal';
import type { RootStackParamList } from '~/navigation/types';
import { useTakesCareFormStore } from '~/stores/marisaTakesCareForm';
import { useCheckout, useSalesFunnel } from '~/stores';
import useAppNavigation from '~/hooks/useAppNavigation';
import useAlertReload from '~/hooks/useAlertReload';
import type { StepContent } from '~/components/StepsForm';
import { TakesCareStepNames } from '~/utils/salesFunnel';

const Contract: React.FC = () => {
  const { navigate, setOptions, push } = useAppNavigation();
  const { newStepLog } = useSalesFunnel();
  const { setInsuranceFormData } = useCheckout();

  useAlertReload();

  const { params } =
    useRoute<
      RouteProp<RootStackParamList, 'MarisaServicos_MarisaTakesCareContract'>
    >();

  const {
    states: { firstStep, secondStep, thirdStep },
  } = useTakesCareFormStore();

  const [modalVisible, setModalVisible] = useState(false);

  const currentStep = useMemo(() => {
    return Number(params?.step || 1);
  }, [params?.step]);

  const goToStep = useCallback(
    (step: number) => {
      push('MarisaServicos_MarisaTakesCareContract', {
        step,
      });
    },
    [push]
  );

  const goToNextStep = useCallback(() => {
    goToStep(currentStep + 1);
  }, [currentStep, goToStep]);

  const onFinish = useCallback(() => {
    setInsuranceFormData({ firstStep, secondStep, thirdStep });

    navigate('MarisaServicos_Checkout', { type: 'marisa-cuida' });
  }, [setInsuranceFormData, firstStep, secondStep, thirdStep, navigate]);

  const stepsFormData = useMemo<StepContent[]>(
    () => [
      {
        step: 1,
        content: <FirstStep onContinue={goToNextStep} />,
        stepName: TakesCareStepNames.PERSONAL_INFO,
      },
      {
        step: 2,
        content: <SecondStep onContinue={goToNextStep} />,
        stepName: TakesCareStepNames.PERSONAL_DATA,
      },
      {
        step: 3,
        content: <ThirdStep onContinue={goToNextStep} />,
        stepName: TakesCareStepNames.ADDRESS,
      },
      {
        step: 4,
        content: <FourthStep onContinue={onFinish} />,
        stepName: TakesCareStepNames.RESUME,
      },
    ],
    [goToNextStep, onFinish]
  );

  const currentStepFormData = useMemo(
    () => stepsFormData.find((data) => data.step === currentStep),
    [currentStep, stepsFormData]
  );

  useEffect(() => {
    const steps: any[] = [firstStep, secondStep, thirdStep];

    if (
      currentStep >= 2 &&
      Object.entries(steps[currentStep - 2]).length === 0
    ) {
      navigate('MarisaServicos_MarisaTakesCareContract', {});
    }
  }, [currentStep, firstStep, navigate, secondStep, thirdStep]);

  useEffect(() => {
    setOptions({
      // headerTitle: () => <Logo onPress={() => setModalVisible(true)} />,
    });
  }, [setOptions]);

  useEffect(() => {
    if (currentStepFormData?.stepName) {
      newStepLog(currentStepFormData.stepName);
    }
  }, [currentStepFormData?.stepName, newStepLog]);

  return (
    <View className="flex-1 bg-light-100">
      <ExitConfirmationModal
        visible={modalVisible}
        onDismiss={() => setModalVisible(false)}
      />

      <StepsForm data={stepsFormData} currentStep={currentStep} />
    </View>
  );
};

export default Contract;
